"use client";

import { useAuthentication } from "@/hooks/framework/useAuthentication";
import useAuthorise from "@/hooks/framework/useAuthorise";
import { useEventBookmarks } from "@/hooks/rangers/useEventBookmarks";
import { useEvents } from "@/hooks/rangers/useEvents";
import { ParticipantStatus, RangerEvents } from "@/prisma/generated/browser";
import {
  Box,
  Button,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Typography
} from "@mui/material";
import { useMemo, useState } from "react";
import EventsTableRow from "./rows/EventsTableRow";
import TableFilter from "./TableFilter";

const additionalFilters = {
  official: { name: "Official Event", value: null },
  autoAccept: { name: "Auto-Accept Enabled", value: null },
  paid: { name: "Paid Event", value: null },
  armyListSubmission: { name: "Army List Collection", value: null },
  externalSignup: { name: "External Signup Required", value: null },
  fullEvent: { name: "Full Events", value: null }
};

type props = {
  showFilter?: boolean;
  userId?: string;
  dateDescending?: boolean;
  showHostEvent?: boolean;
  showIfParticipant?: boolean;
  showIfHostOrParticipant?: boolean;
  showIfBookmarked?: boolean;
  showCompleted?: boolean;
};

export default function EventsTable({
  showFilter = true,
  userId,
  dateDescending = false,
  showHostEvent = false,
  showIfParticipant = false,
  showIfHostOrParticipant = false,
  showIfBookmarked = false,
  showCompleted = false
}: props) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterOverride, setFilterOverride] = useState<typeof events | null>(null);
  const { data: bookmarks } = useEventBookmarks();
  const { data: events, isLoading: isEventsLoading } = useEvents();
  const authorise = useAuthorise();
  const auth = useAuthentication();
  const bookmarkedEventIds = useMemo(() => new Set(bookmarks), [bookmarks]);

  const changePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const changeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const defaultFiltered = useMemo(() => {
    if (!events) return [];
    return events.filter((event) => {
      const isParticipant = !!event.participants.find(
        (participant) =>
          participant.userId === auth.userId && participant.status === ParticipantStatus.Attending
      );
      const isHost = event.userId === auth.userId;

      const matchesUser = userId ? event.userId === userId : true;
      const matchesCompletion = showCompleted ? event.endDate < new Date() : event.endDate >= new Date();
      const matchesParticipant = showIfParticipant ? isParticipant : true;
      const matchesHostOrParticipant = showIfHostOrParticipant ? isHost || isParticipant : true;
      const matchesBookmarked = showIfBookmarked ? bookmarkedEventIds.has(event.id) : true;

      return (
        matchesUser &&
        matchesCompletion &&
        matchesHostOrParticipant &&
        matchesParticipant &&
        matchesBookmarked
      );
    });
  }, [
    events,
    auth.userId,
    userId,
    showCompleted,
    showIfParticipant,
    showIfHostOrParticipant,
    showIfBookmarked
  ]);

  const filteredData = filterOverride ?? defaultFiltered;
  const totalCount = filteredData.length;

  const sortEvents = (a: RangerEvents, b: RangerEvents) => {
    let output: number;

    if (a.official && b.official) output = a.date < b.date ? -1 : 1;
    if (a.official) output = -1;
    if (b.official) output = 1;
    output = a.date < b.date ? -1 : 1;

    if (dateDescending) {
      output = output * -1;
    }
    return output;
  };

  function filter(text: string, country: string, filters?: { [key: string]: boolean | null }) {
    const lowerText = text.toLowerCase();
    const result = defaultFiltered.filter((event) => {
      const textMatch =
        event.name.toLowerCase().includes(lowerText) || event.city.toLowerCase().includes(lowerText);
      const countryMatch = country ? event.country === country : true;
      const filtersMatch = filters
        ? Object.entries(filters).every(([key, value]) => {
            if (key === "fullEvent") {
              const attendingCount = event.participants.filter(
                (p) => p.status === ParticipantStatus.Attending
              ).length;
              const isFull = attendingCount >= event.maxParticipants;
              return value === null || isFull === value;
            }
            return event[key] === value || value === null;
          })
        : true;
      return textMatch && countryMatch && filtersMatch;
    });
    setFilterOverride(result.length > 0 || lowerText || country || filters ? result : null);
    setPage(0);
  }

  const { bookmarkedEvents, unbookmarkedEvents } = useMemo(() => {
    const bookmarked: RangerEvents[] = [];
    const unbookmarked: RangerEvents[] = [];

    for (const event of filteredData) {
      if (bookmarkedEventIds.has(event.id)) {
        bookmarked.push(event);
      } else {
        unbookmarked.push(event);
      }
    }

    bookmarked.sort(sortEvents);
    unbookmarked.sort(sortEvents);

    return {
      bookmarkedEvents: bookmarked,
      unbookmarkedEvents: unbookmarked
    };
  }, [filteredData, bookmarkedEventIds, dateDescending]);

  const emptyState = (
    <>
      <Typography align="center">No events to display!</Typography>
      {authorise("ranger") && showHostEvent && (
        <Stack sx={{ alignItems: "center", mt: 2 }}>
          <Button href="/rangers/create" variant="contained">
            Why not host one?
          </Button>
        </Stack>
      )}
    </>
  );

  const pagination = totalCount > 10 && (
    <TablePagination
      component="div"
      count={totalCount}
      page={page}
      onPageChange={changePage}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={changeRowsPerPage}
    />
  );

  if (isEventsLoading || !events) return <LinearProgress />;

  return (
    <Box>
      {showFilter && <TableFilter filter={filter} additionalFilters={additionalFilters} />}
      {filteredData.length === 0 && <Box sx={{ py: 2 }}>{emptyState}</Box>}
      {filteredData.length > 0 && (
        <>
          <Stack sx={{ gap: 1, display: { xs: "flex", sm: "none" } }}>
            {bookmarkedEvents.map((event) => (
              <EventsTableRow mobile key={event.id} event={event} />
            ))}
            {unbookmarkedEvents.map((event) => (
              <EventsTableRow mobile key={event.id} event={event} />
            ))}
          </Stack>
          <TableContainer sx={{ display: { xs: "none", sm: "block" } }}>
            <Table>
              <TableBody>
                {bookmarkedEvents.map((event) => (
                  <EventsTableRow key={event.id} event={event} />
                ))}
                {unbookmarkedEvents.map((event) => (
                  <EventsTableRow key={event.id} event={event} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {pagination}
    </Box>
  );
}
